
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { reportUserError } from "@/lib/report-error";

interface ProjetoFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface Agencia {
  id: string;
  nome: string;
}

interface Freelancer {
  id: string;
  nome: string;
}

interface AreaAtuacao {
  id: string;
  nome: string;
}

export function ProjetoForm({ onSuccess, onCancel }: ProjetoFormProps) {
  const [searchAgencia, setSearchAgencia] = useState("");
  const [searchFreelancer, setSearchFreelancer] = useState("");
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    agencia_id: "",
    freelancer_id: "",
    area_atuacao_id: "",
    data_inicio: "",
    data_conclusao: "",
    duracao: "",
    status: "em_andamento",
  });

  const { 
    data: agencias = [], 
    isLoading: isLoadingAgencias,
    error: errorAgencias,
    refetch: refetchAgencias
  } = useQuery({
    queryKey: ["agencias", searchAgencia],
    queryFn: async () => {
      const query = supabase
        .from("agencias")
        .select("id, nome")
        .order("nome");
      
      if (searchAgencia) {
        query.ilike("nome", `%${searchAgencia}%`);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  const { 
    data: freelancers = [], 
    isLoading: isLoadingFreelancers,
    error: errorFreelancers,
    refetch: refetchFreelancers
  } = useQuery({
    queryKey: ["freelancers", searchFreelancer],
    queryFn: async () => {
      const query = supabase
        .from("freelancers")
        .select("id, nome")
        .order("nome");
      
      if (searchFreelancer) {
        query.ilike("nome", `%${searchFreelancer}%`);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  const { 
    data: areasAtuacao = [],
    isLoading: isLoadingAreas,
  } = useQuery({
    queryKey: ["areas_atuacao", "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("areas_atuacao")
        .select("id, nome")
        .eq("is_active", true)
        .order("nome");

      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 15, // 15 minutos
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { error } = await supabase
        .from("projetos")
        .insert([formData]);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Projeto cadastrado com sucesso!",
      });
      
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Erro ao criar projeto:", error);
      toast({
        title: "Erro",
        description: "Não foi possível cadastrar o projeto. " + reportUserError(error, { source: "projeto-form" }).description,
        variant: "destructive",
      });
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'data_inicio' | 'data_conclusao') => {
    const value = e.target.value;
    
    const masked = value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1/$2')
      .replace(/(\d{2})(\d)/, '$1/$2')
      .slice(0, 10);

    e.target.value = masked;

    if (masked.length === 10) {
      const [day, month, year] = masked.split('/');
      const isoDate = `${year}-${month}-${day}`;
      setFormData({ ...formData, [field]: isoDate });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Agência</Label>
        <Select
          value={formData.agencia_id}
          onValueChange={(value) =>
            setFormData({ ...formData, agencia_id: value })
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Selecione a agência" />
          </SelectTrigger>
          <SelectContent>
            <div className="sticky top-0 p-2 bg-background border-b">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar agência..."
                  value={searchAgencia}
                  onChange={(e) => setSearchAgencia(e.target.value)}
                  // Evita o crash de focus do typeahead nativo do Radix Select
                  // (item filtrado/desmontado). Só bloqueia caracteres imprimíveis.
                  onKeyDown={(e) => {
                    if (e.key.length === 1) e.stopPropagation();
                  }}
                  className="pl-8"
                />
              </div>
            </div>
            <ScrollArea className="h-[200px]">
              <div className="p-2">
                {isLoadingAgencias ? (
                  <div className="flex flex-col items-center justify-center p-4 space-y-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">
                      Carregando agências...
                    </p>
                  </div>
                ) : errorAgencias ? (
                  <div className="p-4 text-center space-y-2">
                    <p className="text-sm text-destructive">
                      Erro ao carregar agências
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => refetchAgencias()}
                    >
                      Tentar novamente
                    </Button>
                  </div>
                ) : agencias.length > 0 ? (
                  agencias.map((agencia) => (
                    <SelectItem key={agencia.id} value={agencia.id}>
                      {agencia.nome}
                    </SelectItem>
                  ))
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    Nenhuma agência encontrada
                  </div>
                )}
              </div>
            </ScrollArea>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Freelancer</Label>
        <Select
          value={formData.freelancer_id}
          onValueChange={(value) =>
            setFormData({ ...formData, freelancer_id: value })
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Selecione o freelancer" />
          </SelectTrigger>
          <SelectContent>
            <div className="sticky top-0 p-2 bg-background border-b">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar freelancer..."
                  value={searchFreelancer}
                  onChange={(e) => setSearchFreelancer(e.target.value)}
                  // Evita o crash de focus do typeahead nativo do Radix Select
                  // (item filtrado/desmontado). Só bloqueia caracteres imprimíveis.
                  onKeyDown={(e) => {
                    if (e.key.length === 1) e.stopPropagation();
                  }}
                  className="pl-8"
                />
              </div>
            </div>
            <ScrollArea className="h-[200px]">
              <div className="p-2">
                {isLoadingFreelancers ? (
                  <div className="flex flex-col items-center justify-center p-4 space-y-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">
                      Carregando freelancers...
                    </p>
                  </div>
                ) : errorFreelancers ? (
                  <div className="p-4 text-center space-y-2">
                    <p className="text-sm text-destructive">
                      Erro ao carregar freelancers
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => refetchFreelancers()}
                    >
                      Tentar novamente
                    </Button>
                  </div>
                ) : freelancers.length > 0 ? (
                  freelancers.map((freelancer) => (
                    <SelectItem key={freelancer.id} value={freelancer.id}>
                      {freelancer.nome}
                    </SelectItem>
                  ))
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    Nenhum freelancer encontrado
                  </div>
                )}
              </div>
            </ScrollArea>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Área de Atuação</Label>
        <Select
          value={formData.area_atuacao_id}
          onValueChange={(value) =>
            setFormData({ ...formData, area_atuacao_id: value })
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Selecione a área" />
          </SelectTrigger>
          <SelectContent>
            <ScrollArea className="h-[200px]">
              <div className="p-2">
                {isLoadingAreas ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  </div>
                ) : (
                  areasAtuacao.map((area) => (
                    <SelectItem key={area.id} value={area.id}>
                      {area.nome}
                    </SelectItem>
                  ))
                )}
              </div>
            </ScrollArea>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Data de Início</Label>
          <Input
            placeholder="DD/MM/YYYY"
            onChange={(e) => handleDateChange(e, 'data_inicio')}
            maxLength={10}
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label>Data de Conclusão</Label>
          <Input
            placeholder="DD/MM/YYYY"
            onChange={(e) => handleDateChange(e, 'data_conclusao')}
            maxLength={10}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Duração</Label>
        <Input
          value={formData.duracao}
          onChange={(e) =>
            setFormData({ ...formData, duracao: e.target.value })
          }
          placeholder="Ex: 3 meses"
        />
      </div>

      <div className="space-y-2">
        <Label>Status</Label>
        <Select
          value={formData.status}
          onValueChange={(value) =>
            setFormData({ ...formData, status: value })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecione o status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="em_andamento">Em Andamento</SelectItem>
            <SelectItem value="concluido">Concluído</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit">
          Cadastrar Projeto
        </Button>
      </div>
    </form>
  );
}
